import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import {
  Plus,
  Edit,
  Trash2,
  Check,
  XCircle,
  CreditCard,
  DollarSign,
  Calendar,
  Users,
  ListChecks,
} from "lucide-react";
import Breadcrumb from "../../components/ui/Breadcrumb";
import { useSubscription } from "../../hooks/useSubscriptions";
import { useQuery } from "@tanstack/react-query";
import SubscriptionForm from "./SubscriptionForm";
import Skeleton from "../../components/ui/Skeleton";
import ActionButton from "./ActionButton";
import { usePet } from "../../hooks/usePet";

function SubscriptionManagement() {
  const { logout } = useAuth();
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("");
  const [permission, setPermission] = useState({});
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const {
    createSubscription,
    deleteSubscription,
    fetchSubscription,
    updateSubscription,
  } = useSubscription();
  const {
      fetchPet, 
    } = usePet();

  const openModal = (subscription, mode) => {
    setSelectedSubscription(subscription);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSubscription(null);
  };

  const subscriptionFetch = useQuery({
    queryKey: ["subscription"],
    queryFn: fetchSubscription,
  });
  useEffect(() => {
    if (!subscriptionFetch.isLoading) {
      setSubscriptions(subscriptionFetch?.data?.data?.subscriptions);
    }
  }, [subscriptionFetch.isLoading]);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* <Breadcrumb
          items={[
            { label: "Home", to: "/" },
            { label: "Subscription Management" },
          ]}
        /> */}
        {subscriptionFetch.isLoading ? (
          <Skeleton.Card header rows={5} />
        ) : (
          <main className="">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h1 className="text-2xl font-bold">Subscription Management</h1>
              {subscriptions.length >= 3 ? (
                <></>
              ) : (
                permission.add && (
                  <button
                    onClick={() => openModal(null, "create")}
                    className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Subscription Plan
                  </button>
                )
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {subscriptions.map((subscription) => (
                <div
                  key={subscription._id}
                  className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100"
                >
                  <div
                    className={`p-4 ${
                      subscription.title.includes("Premium")
                        ? "bg-primary-50"
                        : "bg-gray-50"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold">
                        {subscription.title}
                      </h3>
                      {subscription.price > 0 && (
                        <span className="flex items-center">
                          <DollarSign className="h-4 w-4 text-primary" />
                          <span className="font-bold text-lg">
                            {subscription.price.toFixed(2)}
                          </span>
                          <span className="text-gray-500 text-sm ml-1">
                            /{subscription?.billing_cycle?.toLowerCase()}
                          </span>
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mt-1">
                      {subscription.description}
                    </p>
                  </div>

                  <div className="p-4">
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <ListChecks className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium">Features</span>
                      </div>
                      <ul className="space-y-2">
                        {subscription.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                            <span className="text-sm text-gray-600">
                              {feature.title}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={`flex items-center mb-4 `}>
                      <Users
                        className={`h-4 w-4 text-gray-500 `}
                        style={subscription?.title === "Basic Plan" ? { visibility: "hidden" } : {}}
                      /> &nbsp;
                      {subscription?.title !== "Basic Plan" && (
                        <span className="text-sm text-gray-600">
                           {subscription.userCount} active users
                        </span>
                      )}
                    </div>

                    <ActionButton
                      openModal={openModal}
                      item={subscription}
                      setPermission={setPermission}
                    />
                  </div>
                </div>
              ))}
            </div>
          </main>
        )}
      </div>

      {/* Subscription Modal */}
      {showModal && (
        <div className=" fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-lg max-w-2xl w-full mx-4 shadow-xl overflow-scroll">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Subscription Plan Details"
                  : modalMode === "edit"
                  ? "Edit Subscription Plan"
                  : "Create Subscription Plan"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <SubscriptionForm
              onCancel={() => {
                closeModal();
              }}
              submissionLoading={submissionLoading}
              onSubmit={async (data) => {
                setSubmissionLoading(true);
                try {
                  if (modalMode === "edit") {
                    await updateSubscription({
                      id: selectedSubscription._id,
                      userData: data,
                    });
                    setSubscriptions((prev) =>
                      prev.map((sub) =>
                        sub._id === selectedSubscription._id
                          ? { ...sub, ...data }
                          : sub
                      )
                    );
                  } else if (modalMode === "create") {
                    const created = await createSubscription(data);
                    setSubscriptions((prev) => [...prev, created]);
                  }
                  closeModal();
                } catch (error) {
                  console.error("Failed to save subscription:", error);
                } finally {
                  setSubmissionLoading(false);
                }
              }}
              defaultValues={selectedSubscription}
              mode={modalMode}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionManagement;
